import { ReactNode, useEffect, useId, useRef } from 'react';

import '../../DashboardV2/dashboardV2.css';
import '../../PaySurge/paySurge.css';

/*
 * GRAVY-10898 — the on/off switch and the yellow inline warning used by the
 * pool automation settings. No prototype exists; these are the Auto backups
 * pieces (BackupInvites/components/BackupsSetting): the surge pay switch
 * (`.paysurge-switch`), the `.dashv2-bkpset*` row and the review modal's
 * inline confirm on the yellow tokens. Wrapped in `.dashv2-scope`, never the
 * `.dashv2` root class, which marks the dashboard itself.
 *
 * The warning focuses Cancel so Enter on a just-opened warning never commits,
 * and Escape closes the warning only (see InlineWarning).
 */

type WarningProps = {
  title: string;
  children: ReactNode;
  goLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
  testId: string;
  className?: string;
};

export const InlineWarning = ({
  title,
  children,
  goLabel,
  onCancel,
  onConfirm,
  testId,
  className = '',
}: WarningProps) => {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  // Escape closes THIS warning only. On window, in the capture phase, so it
  // runs before the document-level Escape handlers of whatever it sits in (the
  // client modal, the pool auto-invite modal, the Shift Detail panel) and
  // stops the event before one of them closes too.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      e.preventDefault();
      onCancel();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onCancel]);

  return (
    <div
      className={`dashv2-scope dashv2-review-confirm dashv2-backup-confirm ${className}`.trim()}
      role="alertdialog"
      aria-label={title}
      data-testid={testId}
    >
      <p className="dashv2-backup-confirm-title">{title}</p>
      {children}
      <div className="dashv2-review-confirm-actions">
        <button
          ref={cancelRef}
          type="button"
          className="dashv2-review-confirm-cancel"
          data-testid={`${testId}-cancel`}
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="dashv2-review-confirm-go"
          data-testid={`${testId}-go`}
          onClick={onConfirm}
        >
          {goLabel}
        </button>
      </div>
    </div>
  );
};

type SwitchProps = {
  label: string;
  value: boolean;
  saving: boolean;
  /** A warning is open: the switch waits for it. */
  pending: boolean;
  error: string | null;
  onToggle: (next: boolean) => void;
  /** Quiet notes under the switch, each with its own test id. */
  meta?: { text: string; testId: string }[];
  testId: string;
};

/** Label, switch and On/Off, then the quiet meta line and any save error. */
export const AutomationSwitch = ({
  label,
  value,
  saving,
  pending,
  error,
  onToggle,
  meta = [],
  testId,
}: SwitchProps) => {
  const labelId = useId();
  return (
    <div
      className="dashv2-scope dashv2-bkpset"
      data-testid={testId}
      data-value={value ? 'on' : 'off'}
    >
      <div className="dashv2-bkpset-row">
        <span className="dashv2-bkpset-label" id={labelId}>
          {label}
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={value}
          aria-labelledby={labelId}
          className="paysurge-switch"
          data-testid={`${testId}-toggle`}
          disabled={saving || pending}
          onClick={() => onToggle(!value)}
        />
        <span className="dashv2-bkpset-state" data-testid={`${testId}-state`}>
          {saving ? 'Saving…' : value ? 'On' : 'Off'}
        </span>
      </div>
      {meta.length > 0 && (
        <div className="dashv2-bkpset-meta">
          {meta.map((m) => (
            <span key={m.testId} data-testid={m.testId}>
              {m.text}
            </span>
          ))}
        </div>
      )}
      {error && (
        <p
          className="dashv2-bkpset-error"
          role="alert"
          data-testid={`${testId}-error`}
        >
          {error}
        </p>
      )}
    </div>
  );
};
